'use client';

import { Check } from '@untitledui/icons';

export interface Step {
  /** Libellé affiché sous la pastille. */
  label: string;
}

interface StepperProps {
  steps: readonly Step[];
  /** Index (base 0) de l'étape en cours. */
  current: number;
}

/**
 * Fil d'Ariane numéroté de l'assistant. Untitled UI n'a pas d'équivalent :
 * `progress-indicators` ne propose que des barres et des cercles de progression.
 */
export function Stepper({ steps, current }: StepperProps) {
  return (
    <ol className="flex w-full items-start">
      {steps.map((step, index) => {
        const isDone = index < current;
        const isCurrent = index === current;

        return (
          <li key={step.label} className="flex flex-1 items-start last:flex-none">
            <div className="flex flex-col items-center gap-2">
              <span
                aria-hidden="true"
                className={[
                  'flex size-8 shrink-0 items-center justify-center rounded-full border text-sm font-semibold transition-colors',
                  isDone && 'border-brand bg-brand-solid text-white',
                  isCurrent && 'border-brand text-brand-secondary bg-primary ring-4 ring-brand/15',
                  !isDone && !isCurrent && 'border-secondary text-quaternary bg-primary',
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                {isDone ? <Check className="size-4" /> : index + 1}
              </span>
              <span
                className={[
                  'max-w-24 text-center text-xs font-medium',
                  isCurrent ? 'text-brand-secondary' : 'text-tertiary',
                ].join(' ')}
              >
                {step.label}
              </span>
            </div>

            {index < steps.length - 1 && (
              <span
                aria-hidden="true"
                className={[
                  'mt-4 h-px flex-1',
                  isDone ? 'bg-brand-solid' : 'bg-border-secondary',
                ].join(' ')}
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}
